import { useEffect, useState } from "react";

import { kaiApi } from "@/services/kaiApi";
import { KaiFeedSnapshot } from "./KaiFeedSnapshot";
import { parseFeedSnapshotSpec } from "./kaiFeedTypes";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

type KaiFeedSnapshotSpec = NonNullable<ReturnType<typeof parseFeedSnapshotSpec>>;

/**
 * Resolves a DB-backed `feed_snapshot` artifact by ID. During a live turn the
 * SSE payload may include the full card under `artifact`; on reload we fetch it.
 */
export function KaiFeedSnapshotRef({
  artifactId,
  conversationId,
  cachedArtifact,
}: {
  artifactId: string;
  conversationId?: string | null;
  cachedArtifact?: unknown;
}) {
  const cachedSpec = cachedArtifact
    ? parseFeedSnapshotSpec(JSON.stringify(cachedArtifact))
    : null;

  const [spec, setSpec] = useState<KaiFeedSnapshotSpec | null>(cachedSpec);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (spec || !conversationId || !artifactId) return;
    let cancelled = false;
    (async () => {
      try {
        const artifact = await kaiApi.getArtifact(conversationId, artifactId);
        const parsed = parseFeedSnapshotSpec(JSON.stringify(artifact.payload));
        if (cancelled) return;
        if (parsed) setSpec(parsed);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, conversationId, artifactId]);

  if (spec) return <KaiFeedSnapshot spec={spec} />;
  if (failed) return <KaiBlockError tag="feed_snapshot" />;
  return <KaiBlockLoading label="Loading feed…" />;
}
